import { GovFormWeb } from '@gov-design-system-ce/react-form';
import { useLocation } from 'react-router-dom';
import { CmsPageRender } from '@gov-nx/component/web';
import { useAuthorizedLabel, useLocale } from '@gov-nx/core/service';
import { PageCode, useMojeAutorizaceDetailContext } from '@gov-nx/module/page';
import {
	Button,
	ButtonGroupLayout,
	ContentLayout,
	ErrorStatusBanner,
	Icon,
	InfoBanner,
	Infobar,
	LayoutGap,
} from '@gov-nx/ui/web';
import { Form602Attachments } from './Form602Attachments';
import { MyAuthorizationDetailSkeleton } from './MyAuthorizationDetailSkeleton';
import { SouhlasCheckbox } from '../../../Autorizace/SouhlasCheckbox';
import { AUTHORIZATION_FAILED_STATE_KEY } from '../../../Autorizace/constants';

export const MyAuthorizationDetailView = () => {
	const {
		code,
		form602Submission,
		formDefinition,
		isProcessing,
		deleteQuery,
		sendQuery,
		handleSubmit,
		isLowAuthorization,
		isAuthorizationEnforced,
	} = useMojeAutorizaceDetailContext();

	const { t } = useLocale(PageCode['moje-podani']);
	const authLabel = useAuthorizedLabel();
	// Set by the /autorizace return handler when authorization failed for a
	// reason other than user cancellation (branch "zrušena z jiného důvodu").
	// Set by the /autorizace return handler when authorization failed for a
	// reason other than user cancellation (branch "zrušena z jiného důvodu").
	const authorizationFailed = Boolean(
		(useLocation().state as Record<string, unknown> | null)?.[
			AUTHORIZATION_FAILED_STATE_KEY
		]
	);

	const getContent = () => {
		if (form602Submission.isLoading) {
			return <MyAuthorizationDetailSkeleton />;
		}
		if (form602Submission.isNotFoundError) {
			return (
				<ContentLayout>
					<InfoBanner
						headline={t('stav.chyba.autorizace-neexistuje')}
						text={t('stav.chyba.tato-autorizace-neexistuje')}
					/>
				</ContentLayout>
			);
		}
		if (form602Submission.error) {
			return <ErrorStatusBanner code={code} />;
		}

		return (
			<GovFormWeb
				formDefinition={formDefinition}
				onSubmit={handleSubmit}>
				<ContentLayout>
					<LayoutGap gap="l">
						{authorizationFailed ? (
							<InfoBanner
								variant="error"
								headline={t('stav.chyba.autorizace-nepodarila')}
								text={
									<>
										{t('stav.chyba.autorizace-nepodarila-popis')}
										<br />
										<strong>
											{t('stav.chyba.autorizace-nepodarila-sekce')}
										</strong>
									</>
								}
							/>
						) : (
							<Infobar
								color="warning"
								type="bold"
								icon={
									<Icon
										type="components"
										name="exclamation-triangle-fill"
									/>
								}>
								{t('stav.varovani.nutno-autorizovat')}
							</Infobar>
						)}
						<Form602Attachments />

						<SouhlasCheckbox
							show={isLowAuthorization}
							disabled={isProcessing}
						/>

						<ButtonGroupLayout>
							<Button
								onClick={deleteQuery.deleteFormSubmission}
								isLoading={deleteQuery.isLoading}
								isDisabled={isProcessing}
								color="error"
								type="base"
								isExpandedMobile
								aria-label={t('akce.smazat')}>
								{t('akce.smazat')}
							</Button>

							<Button
								nativeType="submit"
								isLoading={sendQuery.isLoading}
								isDisabled={isProcessing}
								color="primary"
								type="solid"
								isExpandedMobile
								aria-label={authLabel(
									t('akce.odeslat'),
									isAuthorizationEnforced
								)}>
								{authLabel(t('akce.odeslat'), isAuthorizationEnforced)}
							</Button>
						</ButtonGroupLayout>
					</LayoutGap>
				</ContentLayout>
			</GovFormWeb>
		);
	};

	return (
		<CmsPageRender
			code={code}
			showDescription={false}
			showFavorite={false}
			override={{
				// TODO: tady prozatim jako jmeno formId, protoze nazev nechodi
				name: form602Submission.data?.nazev ?? form602Submission.data?.formId,
				navigations: [],
			}}>
			{getContent()}
		</CmsPageRender>
	);
};
